import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPin, Plus, Star, Trash2, Pencil, X, Home as HomeIcon, Briefcase, User as UserIcon, ExternalLink } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const emptyAddr = { label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "Telangana", pincode: "", landmark: "", is_default: false, lat: null, lng: null };
const LABELS = [
  { key: "Home", icon: HomeIcon },
  { key: "Work", icon: Briefcase },
  { key: "Other", icon: UserIcon },
];

export function AddressManager({ endpoint, title = "Saved addresses", showTitle = true }) {
  const [addrs, setAddrs] = useState([]);
  const [form, setForm] = useState(emptyAddr);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useMemo(() => async () => {
    try {
      const { data } = await api.get(endpoint);
      setAddrs(data.addresses || []);
    } catch (e) {
      setAddrs([]);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing}`, form);
        toast.success("Address updated");
      } else {
        await api.post(endpoint, form);
        toast.success("Address added");
      }
      setForm(emptyAddr);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const edit = (a) => { setEditing(a.id); setForm({ ...emptyAddr, ...a }); setShowForm(true); };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.label}" address?`)) return;
    await api.delete(`${endpoint}/${a.id}`);
    load();
  };
  const setDefault = async (a) => {
    await api.put(`${endpoint}/${a.id}`, { ...a, is_default: true });
    load();
  };

  return (
    <div>
      {showTitle && (
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl font-extrabold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-hyd-gold" /> {title}
          </h2>
          <span className="text-hyd-text2 text-sm">{addrs.length} saved</span>
        </div>
      )}

      {addrs.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {addrs.map((a) => (
            <div
              key={a.id}
              data-testid={`address-card-${a.id}`}
              className={`bg-hyd-panel border rounded-2xl p-4 flex flex-col gap-2 ${a.is_default ? "border-hyd-gold shadow-glow-gold" : "border-hyd-border"}`}
            >
              <div className="flex items-center gap-2">
                <LabelIcon label={a.label} />
                <span className="font-bold uppercase tracking-widest text-xs text-hyd-gold">{a.label}</span>
                {a.is_default && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-hyd-gold/20 text-hyd-gold ml-1">Default</span>}
                <div className="ml-auto flex gap-1">
                  {!a.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => setDefault(a)} className="text-hyd-text2 hover:text-hyd-gold hover:bg-hyd-bg h-7 px-2">
                      <Star className="w-3.5 h-3.5 mr-1" /> Default
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => edit(a)} className="text-hyd-gold hover:bg-hyd-bg h-7 px-2"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(a)} className="text-hyd-red hover:bg-hyd-bg h-7 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="text-sm">
                <div className="font-semibold">{a.name}</div>
                <div className="text-hyd-text2">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                <div className="text-hyd-text2">{a.city}, {a.state} - {a.pincode}</div>
                <div className="text-hyd-text2">📞 {a.phone}</div>
                {a.landmark && <div className="text-hyd-text3 text-xs">Landmark: {a.landmark}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!showForm ? (
        <Button
          data-testid="address-add-btn"
          onClick={() => { setForm(emptyAddr); setEditing(null); setShowForm(true); }}
          className="mt-4 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full"
        >
          <Plus className="w-4 h-4 mr-1" /> Add new address
        </Button>
      ) : (
        <form onSubmit={submit} className="mt-4 bg-hyd-panel border border-hyd-border rounded-2xl p-4 space-y-3" data-testid="address-form">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">{editing ? "Edit address" : "New address"}</h3>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyAddr); }} className="text-hyd-text2 hover:text-hyd-red"><X className="w-4 h-4" /></button>
          </div>

          <div>
            <Label className="text-xs text-hyd-text2 mb-1 block">Search your area / building</Label>
            <AddressAutocomplete
              placeholder="e.g. Symphony Park Homes, Beeramguda"
              onPick={({ line1, line2, city, state, pincode, lat, lng }) => {
                setForm((f) => ({
                  ...f,
                  line1: line1 || f.line1,
                  line2: line2 || f.line2,
                  city: city || f.city,
                  state: state || f.state,
                  pincode: pincode || f.pincode,
                  lat: lat ?? null,
                  lng: lng ?? null,
                }));
                toast.success("Address auto-filled — review & complete");
              }}
            />
            {form.lat && form.lng && (
              <div className="mt-1 flex items-center gap-2 text-[11px] text-hyd-text3">
                <MapPin className="w-3 h-3 text-hyd-gold" />
                <span>Pinned: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}</span>
                <a href={`https://www.google.com/maps?q=${form.lat},${form.lng}`} target="_blank" rel="noreferrer" className="text-hyd-gold hover:underline inline-flex items-center gap-0.5">
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-hyd-text2 mb-1 block">Label</Label>
            <div className="flex gap-2">
              {LABELS.map((l) => (
                <button
                  type="button"
                  key={l.key}
                  onClick={() => set("label", l.key)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest flex items-center gap-1 ${form.label === l.key ? "border-hyd-gold text-hyd-gold bg-hyd-gold/10" : "border-hyd-border text-hyd-text2 hover:border-hyd-gold/60"}`}
                >
                  <l.icon className="w-3 h-3" /> {l.key}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Full name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} required /></F>
            <F label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} required /></F>
            <F label="Address line 1" full><Input value={form.line1} onChange={(e) => set("line1", e.target.value)} required /></F>
            <F label="Address line 2" full><Input value={form.line2} onChange={(e) => set("line2", e.target.value)} /></F>
            <F label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} required /></F>
            <F label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} required /></F>
            <F label="Pincode"><Input value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} required maxLength={6} /></F>
            <F label="Landmark"><Input value={form.landmark} onChange={(e) => set("landmark", e.target.value)} /></F>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.is_default} onCheckedChange={(v) => set("is_default", !!v)} /> Set as default address
          </label>
          <div className="flex gap-2">
            <Button data-testid="address-save-btn" type="submit" disabled={saving} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full">
              {saving ? "Saving..." : (editing ? "Save changes" : "Add address")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyAddr); }} className="text-hyd-text2">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}

function LabelIcon({ label }) {
  const item = LABELS.find((l) => l.key === label) || LABELS[2];
  const Icon = item.icon;
  return <Icon className="w-4 h-4 text-hyd-gold" />;
}

function F({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text">{children}</div>
    </div>
  );
}
